import {http} from '../http'
import * as facesApi from '../../coreApi/faces'
import {APIResponse} from '../types'
import {PhotoRecognitionAttempts} from '../../../types'
import {handleError} from '../handleError'

interface Params {
  photoId: number
  clientId: number
  allScans?: boolean
}

type ResolvedReturn = APIResponse<PhotoRecognitionAttempts>

export interface ServiceMethod {
  (params: Params): Promise<ResolvedReturn>
}

const ERROR_PATH = 'api.faces.getRecognitionAttempts()'
const GENERIC_ERROR =
  'Something went wrong while loading facial recognition data. Please try again.'

export const getRecognitionAttempts: ServiceMethod = async ({
  photoId,
  clientId,
  allScans,
}) => {
  let resolvedReturn: ResolvedReturn

  try {
    // The faces service enforces per-client authorization (the authoritative
    // `campminderstaff` gate) and returns 403 to unauthorized callers, so the
    // server contract is the source of truth here; the client-side employee
    // affordance is only a UX/defense-in-depth convenience.
    const response = await facesApi.getRecognitionAttempts(http, {
      PhotoID: photoId,
      ClientID: clientId,
      AllScans: allScans,
    })

    if (!response || !response.data) {
      throw new Error('Unsuccessful Get Recognition Attempts')
    }

    resolvedReturn = {
      result: 'success',
      data: {
        photoId: response.data.photoId,
        attemptCount: response.data.attemptCount ?? 0,
        faces: (response.data.faces || []).map((face) => ({
          azureFaceId: face.azureFaceId,
          attemptedUTC: face.attemptedUTC,
          candidates: (face.candidates || []).map((candidate) => ({
            rank: candidate.rank,
            personId: candidate.personId,
            // Normalize the nullable fields at the decode boundary so a field
            // omitted by the server can't leak through as `undefined` (which
            // the null-vs-value UI branches don't expect). `?? null` keeps a
            // real 0 confidence intact while coalescing a missing one to null.
            confidence: candidate.confidence ?? null,
            thresholdUsed: candidate.thresholdUsed,
            accepted: candidate.accepted,
            trainedUTC: candidate.trainedUTC ?? null,
          })),
        })),
      },
    }
  } catch (e) {
    resolvedReturn = handleError(ERROR_PATH, GENERIC_ERROR, e)
  }

  return Promise.resolve(resolvedReturn)
}
